// src/components/tickets/detail/AssignmentHistory.tsx
//
// The assignment-history timeline (Story 4.2 AC 7). Every yak_ticket_assignments
// row, oldest-first: assignee name + position, the layer (resolved from the
// org's rule by layer_id → position label + 1-based index; "—" when NULL or
// since-edited-away), the reason as a human label (assignmentReasonLabel), and
// timestamp. Only `initial` rows exist until 4.4/4.6 ship.
//
// Built as a LIST that Story 4.4 extends (interleaving escalation context notes)
// — never restructured.

import { assignmentReasonLabel, personName } from '../../../utils/ticketDisplay'
import { resolveLayerDisplay } from '../../../utils/fieldDisplay'
import { matchEscalationNotes } from '../../../utils/escalation'
import { formatDateTime } from '../../../utils/dateUtils'
import type {
  YakAssignmentWithUser,
  YakMessageWithAuthor,
  YakRoutingRule,
} from '../../../types/yak'

interface AssignmentHistoryProps {
  assignments: YakAssignmentWithUser[]
  rule: YakRoutingRule | null
  // The thread, for interleaving escalation context notes (Story 4.4 AC 8).
  messages: YakMessageWithAuthor[]
}

export function AssignmentHistory({
  assignments,
  rule,
  messages,
}: AssignmentHistoryProps) {
  // An 'escalated' row's context note is the internal_note inserted in the same
  // transaction (created_at-equal). Keyed by assignment id (Story 4.4 AC 8).
  const escalationNotes = matchEscalationNotes(assignments, messages)
  return (
    <section className="card p-5" aria-labelledby="assignment-heading">
      <h2
        id="assignment-heading"
        className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500"
      >
        Assignment history
      </h2>

      {assignments.length === 0 ? (
        <p className="text-sm text-gray-500">No assignments recorded.</p>
      ) : (
        <ol className="space-y-3">
          {assignments.map(a => {
            const layer = resolveLayerDisplay(rule?.layers, a.layer_id)
            const who = a.assignee ? personName(a.assignee) : '—'
            const position = a.assignee?.position ?? null
            const note = escalationNotes[a.id]
            return (
              <li key={a.id} className="text-sm">
                <div className="font-medium text-gray-900">
                  {who}
                  {position ? (
                    <span className="font-normal text-gray-500">
                      {' '}
                      · {position}
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-gray-500">
                  {assignmentReasonLabel(a.reason)}
                  {' · '}
                  {layer ? `${layer.position} (layer ${layer.index})` : '—'}
                  {a.created_at ? ` · ${formatDateTime(a.created_at)}` : ''}
                </div>
                {note ? (
                  // A compact echo of the escalation note (the same content also
                  // renders yellow-walled in the thread; one row, two surfaces).
                  <p className="mt-1 border-l-2 border-yellow-300 bg-yellow-50 px-2 py-1 text-xs italic text-gray-600">
                    {note}
                  </p>
                ) : null}
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
